// src/admin/AdminRoutesPage.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
// import CurrencyConverter from '../store/CurrencyConverter';
import RoutePreview from './RoutePreview';
import PricingEngine from '../services/PricingEngine';

const toNumber = (coord, axis) => {
  if (coord == null || coord === '') return '';
  if (typeof coord === 'number' || typeof coord === 'string') return coord;
  if (typeof coord.latitude === 'number') {
    return axis === 'lat' ? coord.latitude : coord.longitude;
  }
  return '';
};

function AdminRoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState({
    id: '',
    name: '',
    cityIds: [],
    creatorId: '', // Creator user ID
    // Creator workflow status
    creatorStatus: 'draft', // draft, submitted, needs_attention, live, off_store
    adminNotes: '', // Notes from admin to creator
    // Store fields
    storeTagline: '',
    storeStatus: 'coming-soon',
    storeBulletPoints: [], // Array of objects with mainText and subText
    storeCoverImage: '', // Changed from storeImages array to single storeCoverImage
    // Simplified profit margin settings
    profitMargin: 50, // Default $50 or 10%
    profitMarginType: 'dollar' // 'dollar' or 'percentage'
  });
  const [cities, setCities] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRoutePreview, setShowRoutePreview] = useState(false);

  // Add pricing preview state
  const [pricingPreview, setPricingPreview] = useState(null);
  const [previewDates, setPreviewDates] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  // Fetch routes and cities on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [routesSnap, citiesSnap] = await Promise.all([
        getDocs(collection(db, 'routes')),
        getDocs(collection(db, 'cities'))
      ]);

      const routesData = routesSnap.docs.map((docSnap) => ({
        ...docSnap.data(),
        id: docSnap.id,
      }));
      setRoutes(routesData);

      const citiesData = citiesSnap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
        latitude: toNumber(d.data().latitude ?? d.data().coordinates, 'lat'),
        longitude: toNumber(d.data().longitude ?? d.data().coordinates, 'lng'),
      }));
      setCities(citiesData);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load routes or cities');
    } finally {
      setLoading(false);
    }
  };

  // Initialize selected route with store fields
  const initializeRoute = () => ({
    id: '',
    name: '',
    cityIds: [],
    creatorId: '',
    creatorStatus: 'draft',
    adminNotes: '',
    storeTagline: '',
    storeStatus: 'coming-soon',
    storeBulletPoints: [],
    storeCoverImage: '',
    cardBackgroundImage: '',
    cardOverlayColor: 'rgba(0, 0, 0, 0.4)',
    cardOverlayOpacity: 0.4,
    profitMargin: 50,
    profitMarginType: 'dollar',
    cardColors: {
      // 3-color semantic system
      primaryColor: '#FFD700',      // Gold - accent/theme color
      secondaryColor: '#FFFFFF',    // White - text/borders
      backgroundColor: '#072231'    // Navy - card background
    }
  });

  // Get associated cities helper
  const getAssociatedCities = (route) => {
    if (!Array.isArray(route.cityIds)) return 'No Cities';
    return route.cityIds
      .map((cid) => {
        const c = cities.find((cc) => cc.id === cid);
        return c ? c.name : 'Unknown City';
      })
      .join(', ');
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      // Delete old image if exists
      if (selectedRoute.storeCoverImage && selectedRoute.storeCoverImage.includes('firebasestorage.googleapis.com')) {
        try {
          const oldImageRef = ref(storage, selectedRoute.storeCoverImage);
          await deleteObject(oldImageRef);
        } catch (err) {
          console.log('Old image might already be deleted:', err);
        }
      }

      const fileName = `routes/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      
      setSelectedRoute(prev => ({
        ...prev,
        storeCoverImage: url
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  // Remove image
  const handleRemoveImage = async () => {
    try {
      if (selectedRoute.storeCoverImage && selectedRoute.storeCoverImage.includes('firebasestorage.googleapis.com')) {
        const imageRef = ref(storage, selectedRoute.storeCoverImage);
        await deleteObject(imageRef).catch(err => {
          console.log('Image might already be deleted:', err);
        });
      }

      setSelectedRoute(prev => ({
        ...prev,
        storeCoverImage: ''
      }));
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  // Handle background image upload
  const handleBackgroundImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      // Delete old background image if exists
      if (selectedRoute.cardBackgroundImage && selectedRoute.cardBackgroundImage.includes('firebasestorage.googleapis.com')) {
        try {
          const oldImageRef = ref(storage, selectedRoute.cardBackgroundImage);
          await deleteObject(oldImageRef);
        } catch (err) {
          console.log('Old background image might already be deleted:', err);
        }
      }

      const fileName = `routes/backgrounds/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      setSelectedRoute(prev => ({
        ...prev,
        cardBackgroundImage: url
      }));
    } catch (error) {
      console.error('Error uploading background image:', error);
      alert('Failed to upload background image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  // Remove background image
  const handleRemoveBackgroundImage = async () => {
    try {
      if (selectedRoute.cardBackgroundImage && selectedRoute.cardBackgroundImage.includes('firebasestorage.googleapis.com')) {
        const imageRef = ref(storage, selectedRoute.cardBackgroundImage);
        await deleteObject(imageRef).catch(err => {
          console.log('Background image might already be deleted:', err);
        });
      }

      setSelectedRoute(prev => ({
        ...prev,
        cardBackgroundImage: ''
      }));
    } catch (error) {
      console.error('Error removing background image:', error);
    }
  };

  // Handle bullet points
  const handleBulletPointChange = (index, field, value) => {
    setSelectedRoute(prev => {
      const newBulletPoints = [...prev.storeBulletPoints];
      newBulletPoints[index] = {
        ...newBulletPoints[index],
        [field]: value
      };
      return { ...prev, storeBulletPoints: newBulletPoints };
    });
  };

  // Add bullet point
  const handleAddBulletPoint = () => {
    setSelectedRoute(prev => ({
      ...prev,
      storeBulletPoints: [...prev.storeBulletPoints, { mainText: '', subText: '' }]
    }));
  };

  // Remove bullet point
  const handleRemoveBulletPoint = (index) => {
    setSelectedRoute(prev => ({
      ...prev,
      storeBulletPoints: prev.storeBulletPoints.filter((_, i) => i !== index)
    }));
  };

  // Add function to preview pricing
  const handlePreviewPricing = async () => {
    if (!selectedRoute.name || !previewDates.start || !previewDates.end) return;

    const result = await PricingEngine.calculatePassPrice(
      selectedRoute,
      previewDates.start,
      previewDates.end,
      selectedRoute.pricingSettings
    );

    setPricingPreview(result);
  };

  // Save route changes
  const handleSaveRouteChanges = async () => {
    try {
      // Auto-link creatorStatus to storeStatus
      let finalStoreStatus = selectedRoute.storeStatus;
      const creatorStatus = selectedRoute.creatorStatus || 'draft';

      // Force hidden for draft, submitted, needs_attention, off_store
      if (['draft', 'submitted', 'needs_attention', 'off_store'].includes(creatorStatus)) {
        finalStoreStatus = 'hidden';
      }
      // If live, admin can choose (keep their selection)

      const routeDoc = {
        name: selectedRoute.name || '', // Standardized field for route title (matches StoreCard)
        cityIds: Array.isArray(selectedRoute.cityIds) ? selectedRoute.cityIds : [],
        creatorId: selectedRoute.creatorId || '',
        creatorStatus: creatorStatus,
        adminNotes: selectedRoute.adminNotes || '',
        storeTagline: selectedRoute.storeTagline || '',
        storeStatus: finalStoreStatus,
        storeBulletPoints: selectedRoute.storeBulletPoints || [],
        storeCoverImage: selectedRoute.storeCoverImage || '',
        cardBackgroundImage: selectedRoute.cardBackgroundImage || '',
        cardOverlayColor: selectedRoute.cardOverlayColor || 'rgba(0, 0, 0, 0.4)',
        cardOverlayOpacity: selectedRoute.cardOverlayOpacity !== undefined ? selectedRoute.cardOverlayOpacity : 0.4,
        profitMargin: selectedRoute.profitMargin || 50,
        profitMarginType: selectedRoute.profitMarginType || 'dollar',
        cardColors: selectedRoute.cardColors || {
          primaryColor: '#FFD700',
          secondaryColor: '#FFFFFF',
          backgroundColor: '#072231'
        }
      };

      // Handle legacy storeImages field if updating existing route
      if (selectedRoute.id) {
        const existingRoute = routes.find(r => r.id === selectedRoute.id);
        if (existingRoute?.storeImages?.length > 0) {
          // Migrate first image to storeCoverImage if no cover image exists
          if (!routeDoc.storeCoverImage && existingRoute.storeImages[0]) {
            routeDoc.storeCoverImage = existingRoute.storeImages[0];
          }
          // Remove old storeImages field
          routeDoc.storeImages = [];
        }
      }


      if (!selectedRoute.id) {
        await addDoc(collection(db, 'routes'), routeDoc);
        alert('Route added successfully');
      } else {
        await updateDoc(doc(db, 'routes', selectedRoute.id), routeDoc);
        alert('Route updated successfully');
      }

      setSelectedRoute(initializeRoute());
      await fetchData(); // Refresh data

    } catch (err) {
      console.error('Error saving route:', err);
      alert('Failed to save route: ' + err.message);
    }
  };

  // Edit route
  const handleEditRoute = (rt) => {
    // Convert legacy bullet points if they're strings
    let bulletPoints = rt.storeBulletPoints || [];
    if (bulletPoints.length > 0 && typeof bulletPoints[0] === 'string') {
      bulletPoints = bulletPoints.map(point => ({ mainText: point, subText: '' }));
    }

    // Debug logging for image loading
    console.log('handleEditRoute - Raw route data:', {
      routeName: rt.name || rt.routeName,
      storeCoverImage: rt.storeCoverImage,
      legacyStoreImages: rt.storeImages,
      allKeys: Object.keys(rt)
    });

    const coverImage = rt.storeCoverImage || (rt.storeImages && rt.storeImages[0]) || '';
    console.log('handleEditRoute - Final coverImage:', coverImage);

    setSelectedRoute({
      id: rt.id,
      name: rt.name || rt.routeName || '', // Support both name (standard) and routeName (legacy)
      cityIds: rt.cityIds || [],
      creatorId: rt.creatorId || '',
      creatorStatus: rt.creatorStatus || 'draft',
      adminNotes: rt.adminNotes || '',
      storeTagline: rt.storeTagline || '',
      storeStatus: rt.storeStatus || 'coming-soon',
      storeBulletPoints: bulletPoints,
      storeCoverImage: coverImage, // Handle legacy storeImages
      cardBackgroundImage: rt.cardBackgroundImage || '',
      cardOverlayColor: rt.cardOverlayColor || 'rgba(0, 0, 0, 0.4)',
      cardOverlayOpacity: rt.cardOverlayOpacity !== undefined ? rt.cardOverlayOpacity : 0.4,
      profitMargin: rt.profitMargin || 50,
      profitMarginType: rt.profitMarginType || 'dollar',
      cardColors: rt.cardColors || {
        primaryColor: '#FFD700',
        secondaryColor: '#FFFFFF',
        backgroundColor: '#072231'
      }
    });
  };

  // Delete route
  const handleDeleteRoute = async (id) => {
    if (!window.confirm('Are you sure you want to delete this route?')) return;

    try {
      const routeToDelete = routes.find(r => r.id === id);
      
      // Delete cover image
      if (routeToDelete?.storeCoverImage && routeToDelete.storeCoverImage.includes('firebasestorage.googleapis.com')) {
        try {
          const imageRef = ref(storage, routeToDelete.storeCoverImage);
          await deleteObject(imageRef);
        } catch (err) {
          console.log('Error deleting image:', err);
        }
      }

      // Delete background image
      if (routeToDelete?.cardBackgroundImage && routeToDelete.cardBackgroundImage.includes('firebasestorage.googleapis.com')) {
        try {
          const imageRef = ref(storage, routeToDelete.cardBackgroundImage);
          await deleteObject(imageRef);
        } catch (err) {
          console.log('Error deleting background image:', err);
        }
      }

      // Also handle legacy storeImages if they exist
      if (routeToDelete?.storeImages?.length) {
        for (const imageUrl of routeToDelete.storeImages) {
          if (imageUrl.includes('firebasestorage.googleapis.com')) {
            try {
              const imageRef = ref(storage, imageUrl);
              await deleteObject(imageRef);
            } catch (err) {
              console.log('Error deleting legacy image:', err);
            }
          }
        }
      }

      await deleteDoc(doc(db, 'routes', id));
      alert('Route deleted successfully');
      await fetchData(); // Refresh data

    } catch (err) {
      console.error('Error deleting route:', err);
      alert('Failed to delete route: ' + err.message);
    }
  };

  // Input handlers
  const handleInputChangeLocal = (e) => {
    const { name, value, type, checked } = e.target;
    setSelectedRoute((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleCitySelectionLocal = (e) => {
    const opts = e.target.options;
    const cityIds = [];
    for (let i = 0; i < opts.length; i++) {
      if (opts[i].selected) {
        cityIds.push(opts[i].value);
      }
    }
    setSelectedRoute((prev) => ({ ...prev, cityIds }));
  };

  if (loading) {
    return <div>Loading Routes & Cities...</div>;
  }

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h2>Manage Routes</h2>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
        <h4>Basic Route Information</h4>
        
        <label style={{ display: 'block', marginBottom: '10px' }}>
          Route Name:
          <input
            type="text"
            name="name"
            value={selectedRoute.name}
            onChange={handleInputChangeLocal}
            style={{ marginLeft: '10px', padding: '5px' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '10px' }}>
          Cities:
          <select
            name="cityIds"
            value={selectedRoute.cityIds}
            onChange={handleCitySelectionLocal}
            multiple
            style={{ marginLeft: '10px', padding: '5px', minHeight: '100px' }}
          >
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.countryCode ? `(${c.countryCode})` : ''}
              </option>
            ))}
          </select>
        </label>

        <h4 style={{ marginTop: '30px' }}>Creator & Store Status</h4>

        <label style={{ display: 'block', marginBottom: '10px' }}>
          Creator ID:
          <input
            type="text"
            name="creatorId"
            value={selectedRoute.creatorId}
            onChange={handleInputChangeLocal}
            placeholder="Creator user UID (leave empty for admin routes)"
            style={{ marginLeft: '10px', padding: '5px', width: '300px' }}
            disabled={!!selectedRoute.id} // Can't change creator once set
          />
        </label>

        <label style={{ display: 'block', marginBottom: '10px' }}>
          <strong>Creator Status:</strong>
          <select
            name="creatorStatus"
            value={selectedRoute.creatorStatus}
            onChange={handleInputChangeLocal}
            style={{ marginLeft: '10px', padding: '5px', width: '180px' }}
          >
            <option value="draft">Draft Saved</option>
            <option value="submitted">Submitted for Review</option>
            <option value="needs_attention">Needs Attention</option>
            <option value="live">Live</option>
            <option value="off_store">Off Store</option>
          </select>
          <span style={{ marginLeft: '10px', fontSize: '0.9rem', color: '#666' }}>
            {selectedRoute.creatorStatus === 'draft' && '(Working draft - not yet submitted)'}
            {selectedRoute.creatorStatus === 'submitted' && '(Awaiting admin review)'}
            {selectedRoute.creatorStatus === 'needs_attention' && '(Requires updates before approval)'}
            {selectedRoute.creatorStatus === 'live' && '(Available in the store)'}
            {selectedRoute.creatorStatus === 'off_store' && '(Not currently available for purchase)'}
          </span>
        </label>

        <label style={{ display: 'block', marginBottom: '10px' }}>
          <strong>Store Status:</strong>
          <select
            name="storeStatus"
            value={selectedRoute.storeStatus}
            onChange={handleInputChangeLocal}
            style={{ marginLeft: '10px', padding: '5px', width: '180px' }}
            disabled={['draft', 'submitted', 'needs_attention', 'off_store'].includes(selectedRoute.creatorStatus)}
          >
            <option value="beta">Beta</option>
            <option value="available">Available</option>
            <option value="coming-soon">Coming Soon</option>
            <option value="hidden">Hidden</option>
          </select>
          <span style={{ marginLeft: '10px', fontSize: '0.9rem', color: '#666' }}>
            (Controls visibility in store)
          </span>
        </label>

        {['draft', 'submitted', 'needs_attention', 'off_store'].includes(selectedRoute.creatorStatus) && (
          <div style={{
            padding: '10px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            marginBottom: '15px',
            fontSize: '0.9rem'
          }}>
            ⚠️ Store Status is auto-set to "hidden" because Creator Status is "{selectedRoute.creatorStatus}"
          </div>
        )}

        <div style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'center' }}>
          <label style={{ display: 'inline-block', textAlign: 'left' }}>
            Admin Notes (visible to creator):
            <textarea
              name="adminNotes"
              value={selectedRoute.adminNotes}
              onChange={handleInputChangeLocal}
              placeholder="Leave notes for the creator about this route..."
              style={{
                padding: '8px',
                width: '500px',
                minHeight: '80px',
                display: 'block',
                marginTop: '5px',
                fontFamily: 'inherit'
              }}
            />
          </label>
        </div>

        <h4 style={{ marginTop: '30px' }}>Store Display Content</h4>

        {selectedRoute.storeStatus !== 'hidden' && (
          <>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              Tagline:
              <input
                type="text"
                name="storeTagline"
                value={selectedRoute.storeTagline}
                onChange={handleInputChangeLocal}
                placeholder="e.g., Experience the untouched beauty..."
                style={{ marginLeft: '10px', padding: '5px', width: '400px' }}
              />
            </label>


            <div style={{ marginBottom: '20px' }}>
              <h5>Bullet Points:</h5>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: '15px'
              }}>
                {selectedRoute.storeBulletPoints?.map((point, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    gap: '10px',
                    width: '600px',
                    padding: '15px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#666' }}>Main Text:</span>
                        <input
                          type="text"
                          value={point.mainText || ''}
                          onChange={(e) => handleBulletPointChange(index, 'mainText', e.target.value)}
                          placeholder="e.g., 10-14 Day Mountain Trek"
                          style={{ 
                            padding: '8px', 
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem'
                          }}
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#666' }}>Subtext:</span>
                        <input
                          type="text"
                          value={point.subText || ''}
                          onChange={(e) => handleBulletPointChange(index, 'subText', e.target.value)}
                          placeholder="e.g., Experience pristine wilderness and dramatic peaks"
                          style={{ 
                            padding: '8px', 
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.95rem'
                          }}
                        />
                      </label>
                    </div>
                    <button
                      onClick={() => handleRemoveBulletPoint(index)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        alignSelf: 'center'
                      }}
                      type="button"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddBulletPoint}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '10px'
                  }}
                  type="button"
                >
                  Add Bullet Point
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h5>Cover Image:</h5>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: '15px'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ marginBottom: '10px' }}
                />
                {uploadingImage && <p>Uploading image...</p>}
                
                {selectedRoute.storeCoverImage && (
                  <div style={{ 
                    position: 'relative',
                    maxWidth: '600px',
                    width: '100%'
                  }}>
                    <img 
                      src={selectedRoute.storeCoverImage} 
                      alt="Route Cover" 
                      style={{ 
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                    <button
                      onClick={handleRemoveImage}
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(220, 53, 69, 0.9)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '35px',
                        height: '35px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Card Background Image Section */}
            <div style={{ marginBottom: '20px' }}>
              <h5>Card Background Image (Optional):</h5>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px' }}>
                Add a background image that fills the entire card behind all UI elements.
              </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '15px'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundImageUpload}
                  disabled={uploadingImage}
                  style={{ marginBottom: '10px' }}
                />

                {selectedRoute.cardBackgroundImage && (
                  <div style={{
                    position: 'relative',
                    maxWidth: '600px',
                    width: '100%'
                  }}>
                    <img
                      src={selectedRoute.cardBackgroundImage}
                      alt="Card Background"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                    <button
                      onClick={handleRemoveBackgroundImage}
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(220, 53, 69, 0.9)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '35px',
                        height: '35px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Overlay Controls */}
                {selectedRoute.cardBackgroundImage && (
                  <div style={{ width: '100%', maxWidth: '600px' }}>
                    <h6 style={{ marginTop: '20px', marginBottom: '10px' }}>Overlay Settings:</h6>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>
                      Adjust the overlay to ensure text readability over the background.
                    </p>

                    <label style={{ display: 'block', marginBottom: '15px' }}>
                      Overlay Color:
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '5px' }}>
                        <select
                          value={selectedRoute.cardOverlayColor || 'rgba(0, 0, 0, 0.4)'}
                          onChange={(e) => setSelectedRoute(prev => ({
                            ...prev,
                            cardOverlayColor: e.target.value
                          }))}
                          style={{
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            flex: 1
                          }}
                        >
                          <option value="rgba(0, 0, 0, 0.4)">Dark (Black)</option>
                          <option value="rgba(255, 255, 255, 0.4)">Light (White)</option>
                          <option value="rgba(7, 34, 49, 0.4)">Navy (Theme)</option>
                          <option value="rgba(255, 215, 0, 0.2)">Gold (Theme)</option>
                        </select>
                      </div>
                    </label>

                    <label style={{ display: 'block', marginBottom: '10px' }}>
                      Overlay Opacity: {selectedRoute.cardOverlayOpacity || 0.4}
                      <input
                        type="range"
                        min="0"
                        max="0.8"
                        step="0.1"
                        value={selectedRoute.cardOverlayOpacity || 0.4}
                        onChange={(e) => {
                          const opacity = parseFloat(e.target.value);
                          setSelectedRoute(prev => ({
                            ...prev,
                            cardOverlayOpacity: opacity
                          }));
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          marginTop: '8px'
                        }}
                      />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.75rem',
                        color: '#999',
                        marginTop: '4px'
                      }}>
                        <span>Transparent</span>
                        <span>Opaque</span>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Comprehensive Card Design System */}
            <div style={{ marginBottom: '20px', marginTop: '30px', background: '#f8f9fa', padding: '25px', borderRadius: '12px' }}>
              <h4 style={{ marginBottom: '5px', color: '#333' }}>🎨 Card Design System</h4>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '25px' }}>
                Complete control over all card colors and design elements
              </p>

              {/* 3-Color System */}
              <div style={{ marginBottom: '30px' }}>
                <h5 style={{ color: '#495057', marginBottom: '15px', paddingBottom: '8px', borderBottom: '2px solid #dee2e6' }}>
                  🎨 Pass Theme Colors
                </h5>
                <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '20px', fontStyle: 'italic' }}>
                  Choose 3 colors to theme your entire pass. Changing a color updates all elements that use it.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>

                  {/* Primary Color */}
                  <div style={{ padding: '15px', border: '2px solid #dee2e6', borderRadius: '8px', background: '#fff' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', fontSize: '1rem', color: '#333' }}>
                      ⭐ Primary Color (Accent/Theme)
                    </label>
                    <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '12px' }}>
                      Buttons, tagline, bullet points, inaugural ribbon
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="color"
                        value={selectedRoute.cardColors?.primaryColor || '#FFD700'}
                        onChange={(e) => setSelectedRoute(prev => ({
                          ...prev,
                          cardColors: { ...prev.cardColors, primaryColor: e.target.value }
                        }))}
                        style={{ width: '60px', height: '45px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '6px' }}
                      />
                      <input
                        type="text"
                        value={selectedRoute.cardColors?.primaryColor || '#FFD700'}
                        onChange={(e) => setSelectedRoute(prev => ({
                          ...prev,
                          cardColors: { ...prev.cardColors, primaryColor: e.target.value }
                        }))}
                        style={{ flex: 1, padding: '10px', border: '1px solid #ced4da', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.95rem' }}
                        placeholder="#FFD700"
                      />
                    </div>
                  </div>

                  {/* Secondary Color */}
                  <div style={{ padding: '15px', border: '2px solid #dee2e6', borderRadius: '8px', background: '#fff' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', fontSize: '1rem', color: '#333' }}>
                      📝 Secondary Color (Text/Borders)
                    </label>
                    <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '12px' }}>
                      Card border, title, flip button, bullet subtext
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="color"
                        value={selectedRoute.cardColors?.secondaryColor || '#FFFFFF'}
                        onChange={(e) => setSelectedRoute(prev => ({
                          ...prev,
                          cardColors: { ...prev.cardColors, secondaryColor: e.target.value }
                        }))}
                        style={{ width: '60px', height: '45px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '6px' }}
                      />
                      <input
                        type="text"
                        value={selectedRoute.cardColors?.secondaryColor || '#FFFFFF'}
                        onChange={(e) => setSelectedRoute(prev => ({
                          ...prev,
                          cardColors: { ...prev.cardColors, secondaryColor: e.target.value }
                        }))}
                        style={{ flex: 1, padding: '10px', border: '1px solid #ced4da', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.95rem' }}
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </div>

                  {/* Background Color */}
                  <div style={{ padding: '15px', border: '2px solid #dee2e6', borderRadius: '8px', background: '#fff' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', fontSize: '1rem', color: '#333' }}>
                      🎭 Background Color
                    </label>
                    <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '12px' }}>
                      Card background, button text (for contrast)
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="color"
                        value={selectedRoute.cardColors?.backgroundColor || '#072231'}
                        onChange={(e) => setSelectedRoute(prev => ({
                          ...prev,
                          cardColors: { ...prev.cardColors, backgroundColor: e.target.value }
                        }))}
                        style={{ width: '60px', height: '45px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '6px' }}
                      />
                      <input
                        type="text"
                        value={selectedRoute.cardColors?.backgroundColor || '#072231'}
                        onChange={(e) => setSelectedRoute(prev => ({
                          ...prev,
                          cardColors: { ...prev.cardColors, backgroundColor: e.target.value }
                        }))}
                        style={{ flex: 1, padding: '10px', border: '1px solid #ced4da', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.95rem' }}
                        placeholder="#072231"
                      />
                    </div>
                  </div>

                </div>
              </div>

              {/* Live Preview - Flippable Card */}
              <div style={{ padding: '20px', background: '#0a0a0a', borderRadius: '8px', border: '2px solid #dee2e6' }}>
                <h6 style={{ marginBottom: '15px', color: '#FFD700', fontSize: '1rem', textAlign: 'center' }}>
                  👁️ Live Preview - Click Card to Flip
                </h6>

                <style>{`
                  /* Custom fonts for preview card */
                  @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap');

                  @font-face {
                    font-family: 'PassFont';
                    src: url('/fonts/PassFont.ttf') format('truetype');
                    font-weight: 400;
                    font-style: normal;
                    font-display: swap;
                  }

                  @font-face {
                    font-family: 'MainFontLight';
                    src: url('/fonts/MainFontLight.ttf') format('truetype');
                    font-weight: 300;
                    font-style: normal;
                    font-display: swap;
                  }

                  /* Preview card container styles */
                  .admin-preview-card-container {
                    width: 300px;
                    height: 485px;
                    margin: 0 auto;
                    perspective: 1000px;
                  }

                  .admin-preview-card {
                    width: 100%;
                    height: 100%;
                    position: relative;
                    transform-style: preserve-3d;
                    -webkit-transform-style: preserve-3d;
                    transition: transform 0.6s;
                  }

                  .admin-preview-card.flipped {
                    transform: rotateY(180deg);
                  }

                  .admin-preview-card-face {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                    border-radius: 12px;
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    cursor: pointer;
                  }

                  .admin-preview-card-front {
                    align-items: center;
                    justify-content: space-between;
                  }

                  .admin-preview-card-back {
                    transform: rotateY(180deg);
                    align-items: center;
                    justify-content: space-between;
                  }
                `}</style>

                {/* Live Preview Using RoutePreview Component */}
                <RoutePreview
                  routeName={selectedRoute.name}
                  showInStore={selectedRoute.showInStore}
                  storeTagline={selectedRoute.storeTagline}
                  storeStatus={selectedRoute.storeStatus}
                  storePrice={selectedRoute.storePrice}
                  storeBulletPoints={selectedRoute.storeBulletPoints}
                  storeImages={selectedRoute.storeCoverImage ? [selectedRoute.storeCoverImage] : []}
                  cardBackgroundImage={selectedRoute.cardBackgroundImage}
                  cardOverlayColor={selectedRoute.cardOverlayColor}
                  cardOverlayOpacity={selectedRoute.cardOverlayOpacity}
                  cardColors={selectedRoute.cardColors}
                />

              </div>
            </div>
          </>
        )}


        {/* Add pricing settings UI after Store Display Settings */}
        {(selectedRoute.storeStatus === 'beta' || selectedRoute.storeStatus === 'available') && (
          <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#e8f4f8', borderRadius: '8px' }}>
            <h4>Simple Profit Margin</h4>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
              Pass Price = Total Daily Allowances + Profit Margin
            </p>
            
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input
                  type="radio"
                  name="profitMarginType"
                  value="dollar"
                  checked={selectedRoute.profitMarginType === 'dollar'}
                  onChange={(e) => setSelectedRoute(prev => ({
                    ...prev,
                    profitMarginType: e.target.value
                  }))}
                />
                Fixed Dollar Amount
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input
                  type="radio"
                  name="profitMarginType"
                  value="percentage"
                  checked={selectedRoute.profitMarginType === 'percentage'}
                  onChange={(e) => setSelectedRoute(prev => ({
                    ...prev,
                    profitMarginType: e.target.value
                  }))}
                />
                Percentage
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label>
                Profit Margin:
                <input
                  type="number"
                  value={selectedRoute.profitMargin || 50}
                  onChange={(e) => setSelectedRoute(prev => ({
                    ...prev,
                    profitMargin: parseFloat(e.target.value) || 0
                  }))}
                  style={{ marginLeft: '10px', padding: '8px', width: '100px' }}
                  min="0"
                  step={selectedRoute.profitMarginType === 'percentage' ? '1' : '5'}
                />
                {selectedRoute.profitMarginType === 'percentage' ? '%' : '$'}
              </label>
              
              <span style={{ fontSize: '0.85rem', color: '#666' }}>
                {selectedRoute.profitMarginType === 'percentage' 
                  ? 'Percentage added to total allowances'
                  : 'Fixed dollar amount added to total allowances'
                }
              </span>
            </div>

            {/* Pricing Preview Tool */}
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px' }}>
              <h5>Test Pricing Calculator</h5>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'end', marginBottom: '15px' }}>
                <label>
                  Start Date:
                  <input
                    type="date"
                    value={previewDates.start}
                    onChange={(e) => setPreviewDates(prev => ({ ...prev, start: e.target.value }))}
                    style={{ display: 'block', marginTop: '5px', padding: '5px' }}
                  />
                </label>
                <label>
                  End Date:
                  <input
                    type="date"
                    value={previewDates.end}
                    onChange={(e) => setPreviewDates(prev => ({ ...prev, end: e.target.value }))}
                    style={{ display: 'block', marginTop: '5px', padding: '5px' }}
                  />
                </label>
                <button
                  onClick={handlePreviewPricing}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Calculate Price
                </button>
              </div>

              {pricingPreview && (
                <div style={{
                  padding: '15px',
                  backgroundColor: '#f0f8ff',
                  borderRadius: '8px',
                  border: '1px solid #007bff'
                }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                    Pass Price: ${pricingPreview.price} USD
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.9rem' }}>
                    <div>Nights: {pricingPreview.summary.nights}</div>
                    <div>Total Allowances: ${pricingPreview.summary.totalDailyAllowances} USD</div>
                    <div>Avg Allowance/Night: ${pricingPreview.summary.avgDailyAllowance} USD</div>
                    <div>Profit Margin: {pricingPreview.summary.routeProfitMargin}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <button 
          onClick={handleSaveRouteChanges}
          style={{ 
            marginTop: '20px', 
            padding: '10px 20px', 
            backgroundColor: '#007bff', 
            color: 'white', 
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          {selectedRoute.id ? 'Update Route' : 'Add Route'}
        </button>

        {selectedRoute.name && (
          <button
            onClick={() => setShowRoutePreview(true)}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#853a38',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Preview in Store
          </button>
        )}
      </div>

      {/* Route Preview Modal */}
      {showRoutePreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: '#1a1a1a',
              padding: '20px',
              borderRadius: '10px',
              maxHeight: '90vh',
              overflowY: 'auto',
              width: '90%',
              maxWidth: '500px',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}
          >
            <h3 style={{ color: '#FFD700', marginBottom: '20px' }}>
              Store Preview: {selectedRoute.name}
            </h3>
            
            <RoutePreview
              routeName={selectedRoute.name}
              showInStore={selectedRoute.storeStatus !== 'hidden'}
              storeTagline={selectedRoute.storeTagline}
              storeStatus={selectedRoute.storeStatus}
              storePrice={selectedRoute.storePrice}
              storeBulletPoints={selectedRoute.storeBulletPoints}
              storeImages={selectedRoute.storeCoverImage ? [selectedRoute.storeCoverImage] : []}
              cardBackgroundImage={selectedRoute.cardBackgroundImage}
              cardOverlayColor={selectedRoute.cardOverlayColor}
              cardOverlayOpacity={selectedRoute.cardOverlayOpacity}
              cardColors={selectedRoute.cardColors || {
                primaryColor: '#FFD700',
                secondaryColor: '#FFFFFF',
                backgroundColor: '#072231'
              }}
            />

            <button
              onClick={() => setShowRoutePreview(false)}
              style={{
                marginTop: '20px',
                backgroundColor: '#853a38',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Close Preview
            </button>
          </div>
        </div>
      )}

      <h4>Current Routes</h4>
      <style>
        {`
          /* Admin-specific currency converter styles */
          .currency-converter {
            display: inline-flex;
            align-items: center;
            gap: 5px;
          }
          
          .currency-converter .price-display {
            font-size: inherit;
            color: inherit;
            font-weight: normal;
          }
          
          .currency-converter .currency-selector {
            background: #f8f9fa;
            border: 1px solid #ced4da;
            color: #495057;
            padding: 2px 5px;
            border-radius: 3px;
            font-size: 0.8rem;
          }
          
          .currency-converter .currency-selector:hover {
            background: #e9ecef;
            border-color: #adb5bd;
          }
        `}
      </style>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Cities</th>
            <th style={{ padding: '10px', textAlign: 'center' }}>Creator Status</th>
            <th style={{ padding: '10px', textAlign: 'center' }}>Store Status</th>
            <th style={{ padding: '10px', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {routes.map((rt) => {
            const associated = getAssociatedCities(rt);
            const statusColors = {
              draft: '#666',
              submitted: '#3b82f6',
              needs_attention: '#f59e0b',
              live: '#10b981',
              off_store: '#ef4444'
            };
            const statusColor = statusColors[rt.creatorStatus] || '#666';

            return (
              <tr key={rt.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px' }}>{rt.name || rt.routeName}</td>
                <td style={{ padding: '10px' }}>{associated}</td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    backgroundColor: statusColor,
                    color: 'white',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {rt.creatorStatus || 'draft'}
                  </span>
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  {rt.storeStatus || 'coming-soon'}
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  <button onClick={() => handleEditRoute(rt)} style={{ marginRight: '5px' }}>Edit</button>
                  <button onClick={() => handleDeleteRoute(rt.id)}>Delete</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AdminRoutesPage;